// frontend/src/pages/WorkOptimizationPage.tsx
import React, { useEffect, useState } from 'react';
import { Button, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import { PlusOutlined, DeleteOutlined, BarChartOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { TimingSessionListItem, getSessions, deleteSession } from '../api/workOptimization';

const { Title } = Typography;

const statusTag = (item: TimingSessionListItem) =>
  item.ended_at
    ? <Tag color="green">Завершена</Tag>
    : <Tag color="blue">В процессе</Tag>;

const fmtDuration = (item: TimingSessionListItem) => {
  if (!item.ended_at) return '—';
  const secs = (new Date(item.ended_at).getTime() - new Date(item.started_at).getTime()) / 1000;
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}м ${s}с`;
};

const WorkOptimizationPage: React.FC = () => {
  const [sessions, setSessions] = useState<TimingSessionListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { setSessions(await getSessions()); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteSession(id);
      setSessions(prev => prev.filter(s => s.id !== id));
      message.success('Удалено');
    } catch { message.error('Ошибка при удалении'); }
  };

  const columns = [
    { title: 'Тип работы', dataIndex: ['work_type', 'name'], key: 'work_type' },
    { title: 'Рабочее место', key: 'workplace', render: (_: any, r: TimingSessionListItem) => r.workplace?.name || '—' },
    { title: 'Сотрудник', key: 'individual', render: (_: any, r: TimingSessionListItem) => r.individual ? `${r.individual.last_name} ${r.individual.first_name}` : '—' },
    { title: 'Авто', dataIndex: 'car_info', key: 'car_info', render: (v: string) => v || '—' },
    { title: 'Дата', key: 'started_at', render: (_: any, r: TimingSessionListItem) => new Date(r.started_at).toLocaleString('ru-RU') },
    { title: 'Длительность', key: 'duration', render: (_: any, r: TimingSessionListItem) => fmtDuration(r) },
    { title: 'Событий', dataIndex: 'event_count', key: 'event_count' },
    { title: 'Статус', key: 'status', render: (_: any, r: TimingSessionListItem) => statusTag(r) },
    {
      title: 'Действия', key: 'actions',
      render: (_: any, r: TimingSessionListItem) => (
        <Space>
          {!r.ended_at && (
            <Button size="small" icon={<PlayCircleOutlined />} onClick={() => navigate(`/work-optimization/session/${r.id}/timing`)}>
              Продолжить
            </Button>
          )}
          {r.ended_at && (
            <Button size="small" icon={<BarChartOutlined />} onClick={() => navigate(`/work-optimization/session/${r.id}/analysis`)}>
              Анализ
            </Button>
          )}
          <Popconfirm title="Удалить сессию?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Хронометраж работ</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/work-optimization/session/new')}>
          Новая сессия
        </Button>
      </div>
      <Table
        rowKey="id"
        dataSource={sessions}
        columns={columns}
        loading={loading}
        pagination={{ pageSize: 20 }}
      />
    </div>
  );
};

export default WorkOptimizationPage;
